import { Tooltip } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getDashboardQuestions } from "../../../Actions/DashboardActions";
import NoData from "../../../components/NoData";

const QuestionTypeBadge = ({ type }) => {
  const getTypeStyle = () => {
    switch (type?.toLowerCase()) {
      case "single":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "multi":
        return "bg-purple-50 text-purple-700 border-purple-200";
      case "numeric":
        return "bg-green-50 text-green-700 border-green-200";
      case "open":
        return "bg-orange-50 text-orange-700 border-orange-200";
      default:
        return "bg-gray-50 text-gray-600 border-gray-200";
    }
  };

  return (
    <span className={`px-2 py-0.5 rounded-md text-[10px] font-[600] border ${getTypeStyle()}`}>
      {type || "Unknown"}
    </span>
  );
};

const MyQuestions = ({ user }) => {
  const navigate = useNavigate();
  const {
    data: myQuestions,
    isLoading: myQuestionsLoading,
    isError: myQuestionsError,
  } = useQuery({
    queryFn: () => getDashboardQuestions(user?.organizationId),
    queryKey: ["dashboardQuestions", user?.organizationId],
    enabled: !!user?.organizationId,
    staleTime: 10000,
    cacheTime: 15000,
  });

  return (
    <div className="w-full bg-white border border-[#E0E0E0] rounded-xl h-[360px] flex flex-col overflow-hidden">
      <div className="px-6 py-4 border-b border-[#E0E0E0] flex items-center justify-between bg-gray-50/50">
        <div>
          <h1 className="text-[#0226BE] text-[16px] font-[500]">Qualification Library</h1>
          <p className="text-[11px] text-[#666] mt-0.5">Your custom questions</p>
        </div>
        <button
          className="text-[#0226BE] text-[13px] font-[500] hover:bg-blue-50 px-3 py-1.5 rounded-lg transition-all cursor-pointer flex items-center gap-1"
          onClick={(e) => navigate("/qualification-library")}
        >
          See all
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      <div className="flex-1 overflow-y-auto containerScroll">
        <table className="w-full">
          <thead className="bg-gray-50/80 sticky top-0 z-10">
            <tr className="border-b border-[#E0E0E0]">
              {["Question Name", "Question Text", "Type", "Status"]?.map((data, ind) => (
                <th
                  className="text-[#555] px-4 py-3 text-[11px] font-[600] text-left uppercase tracking-wide"
                  key={ind}
                >
                  {data}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {myQuestionsLoading ? (
              <tr>
                <td colSpan={4}>
                  <div className="w-full text-center py-6 flex items-center justify-center flex-col space-y-3 h-[240px]">
                    <div className="w-8 h-8 border-2 border-[#0226BE] border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-[#666] text-[13px]">Loading questions...</p>
                  </div>
                </td>
              </tr>
            ) : myQuestionsError ? (
              <tr>
                <td colSpan={4}>
                  <div className="w-full text-center py-6 flex items-center justify-center flex-col space-y-3 h-[240px]">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                      <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <p className="text-[#e12121] text-[13px] font-medium">Something went wrong!</p>
                  </div>
                </td>
              </tr>
            ) : myQuestions?.length ? (
              myQuestions?.map((data, index) => (
                <tr
                  className="border-b border-[#E0E0E0] hover:bg-blue-50/40 transition-colors cursor-pointer group"
                  key={index}
                  onClick={() => navigate("/qualification-library")}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <Tooltip title={data?.questionName || "-"} placement="top">
                        <span className="text-[#333] font-[500] text-[13px] truncate max-w-[150px] group-hover:text-[#0226BE] transition-colors">
                          {data?.questionName || "-"}
                        </span>
                      </Tooltip>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Tooltip title={data?.questionText || "-"} placement="top">
                      <p className="text-[#666] text-[13px] truncate max-w-[300px]">
                        {data?.questionText || "-"}
                      </p>
                    </Tooltip>
                  </td>
                  <td className="px-4 py-3">
                    <QuestionTypeBadge type={data?.questionType} />
                  </td>
                  <td className="px-4 py-3">
                    {data?.questionStatus === "active" ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-50 border border-green-200 text-green-700 text-[10px] font-[600] rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-50 border border-gray-200 text-gray-600 text-[10px] font-[600] rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                        {data?.questionStatus || "Draft"}
                      </span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4}>
                  <NoData
                    msg={
                      <button
                        className="text-[#0226BE] font-[500] hover:underline cursor-pointer"
                        onClick={(e) => navigate("/qualification-library")}
                      >
                        Add your first question
                      </button>
                    }
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MyQuestions;
